
import React, { useState, useEffect } from 'react';
import { geminiService } from '../services/geminiService';

interface SmartImageProps {
  src: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
}

const SmartImage: React.FC<SmartImageProps> = ({ src, alt, className = "", onLoad }) => {
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setError(true);
      return;
    }

    if (src.startsWith('ai:')) {
      const generate = async () => {
        setLoading(true);
        setError(false);
        try {
          const prompt = src.replace('ai:', '');
          const result = await geminiService.generateImage(prompt);
          if (result) {
            setDisplaySrc(result);
          } else {
            // Fallback to high quality abstract studio background
            setDisplaySrc('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop');
          }
        } catch {
          setDisplaySrc('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop');
        }
        setLoading(false);
      };
      generate();
    } else {
      setDisplaySrc(src);
      setError(false);
    }
  }, [src]);

  if (loading) {
    return (
      <div className={`relative bg-zinc-900 overflow-hidden flex items-center justify-center ${className}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>
        <div className="text-center px-6">
          <span className="text-[10px] font-black uppercase tracking-[0.5em] text-primary block mb-2 animate-pulse">Studio AI</span>
          <span className="text-xs font-bold uppercase tracking-widest text-white/40">Visualizing Concept...</span>
        </div>
      </div>
    );
  }

  if (error || !displaySrc) {
    return (
      <div className={`bg-zinc-900/80 backdrop-blur-sm flex flex-col items-center justify-center text-white/30 border border-white/5 ${className}`}>
        <span className="material-symbols-outlined text-3xl mb-1 text-white/20">image</span>
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/20">{alt || 'T.H.E Apps'}</span>
      </div>
    );
  }

  return (
    <img 
      src={displaySrc} 
      alt={alt} 
      referrerPolicy="no-referrer"
      loading="lazy"
      className={`transition-opacity duration-700 ${className}`} 
      onLoad={onLoad}
      onError={() => {
        // Fallback to curated studio dark photography if specific URL fails
        if (displaySrc !== 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop') {
          setDisplaySrc('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop');
        } else {
          setError(true);
        }
      }}
    />
  );
};

export default SmartImage;

